import React, { useState } from 'react';
import { Smartphone, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { whatsappApi } from '../../../services/whatsappApi';

interface ChannelNotConfiguredProps {
  clientId: string;
  /** Optional client profile snapshot — used to pre-fill personalized defaults. */
  businessName?: string;
  botName?: string;
  onCreated: () => void;
}

/**
 * The "Set up WhatsApp" entry point when no channel exists yet.
 *
 * The platform owns ONE Meta App + ONE WABA + ONE phone number (configured
 * in env vars). Clients never enter Meta IDs — those auto-fill server-side.
 * The form just asks for the two real product decisions:
 *   1. Who can chat with the bot? (private / public)
 *   2. What should we say to people who DM but aren't on the roster?
 */
export const ChannelNotConfigured: React.FC<ChannelNotConfiguredProps> = ({
  clientId,
  businessName,
  botName,
  onCreated,
}) => {
  const personalizedDefault = buildDefaultNotEnrolledReply({ businessName, botName });

  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [accessMode, setAccessMode] = useState<'private' | 'public'>('private');
  const [notEnrolledReply, setNotEnrolledReply] = useState(personalizedDefault);

  if (!showForm) {
    return (
      <div className="bg-gradient-to-br from-emerald-50 to-white rounded-2xl border border-emerald-100 p-8 text-center">
        <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
          <Smartphone size={26} className="text-emerald-600" />
        </div>
        <h4 className="font-semibold text-gray-900 text-lg mb-1">
          Activate your WhatsApp agent
        </h4>
        <p className="text-sm text-gray-600 max-w-md mx-auto mb-6 leading-relaxed">
          One click and your AI is reachable on WhatsApp. We've already set up the number
          and the connection — you just decide who can chat and what they hear.
        </p>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-sm whitespace-nowrap"
        >
          <Sparkles size={16} />
          Set up WhatsApp
        </button>
      </div>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // No phone_number_id / waba_id / display_name in the payload — server
      // fills them from env (META_PHONE_NUMBER_ID / META_WABA_ID) and from
      // the client record (bot_name / name) respectively.
      await whatsappApi.createChannel(clientId, {
        access_mode: accessMode,
        not_enrolled_reply:
          accessMode === 'private' ? notEnrolledReply.trim() || undefined : undefined,
        status: 'active',
      });
      toast.success('WhatsApp is live');
      onCreated();
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail || err?.message || 'Failed to set up WhatsApp';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5">
      <div>
        <h4 className="font-semibold text-gray-900">Set up WhatsApp</h4>
        <p className="text-xs text-gray-500 mt-1">
          Two quick choices and you're live. You can change either of these any time.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-800 mb-2">
          Who can chat with your bot?
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(
            [
              {
                mode: 'private' as const,
                title: 'Private',
                desc: 'Only people you add to your roster. Anyone else gets a polite "not enrolled" reply.',
              },
              {
                mode: 'public' as const,
                title: 'Public',
                desc: 'Anyone can DM your number and chat with your AI. Good for support / lead-gen.',
              },
            ]
          ).map(({ mode, title, desc }) => (
            <button
              key={mode}
              type="button"
              onClick={() => setAccessMode(mode)}
              className={`text-left px-4 py-3 rounded-xl border-2 transition-all ${
                accessMode === mode
                  ? 'bg-emerald-50 border-emerald-300 ring-2 ring-emerald-100'
                  : 'bg-white border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-gray-900 capitalize">{title}</span>
                {accessMode === mode && (
                  <span className="text-xs text-emerald-700 font-medium">Selected</span>
                )}
              </div>
              <div className="text-xs text-gray-500 leading-snug">{desc}</div>
            </button>
          ))}
        </div>
      </div>

      {accessMode === 'private' && (
        <div>
          <label className="block text-sm font-medium text-gray-800 mb-1">
            Reply to non-enrolled DMs
          </label>
          <textarea
            value={notEnrolledReply}
            onChange={(e) => setNotEnrolledReply(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
          />
          <p className="text-xs text-gray-400 mt-1">
            Sent automatically when someone who isn't on your roster DMs your number.
            Leave blank to ignore those messages silently.
          </p>
        </div>
      )}

      <div className="flex gap-3 pt-1">
        <button
          type="button"
          onClick={() => setShowForm(false)}
          className="px-4 py-2.5 rounded-xl text-sm font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 shadow-sm whitespace-nowrap"
        >
          {submitting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {submitting ? 'Setting up…' : 'Activate WhatsApp'}
        </button>
      </div>
    </form>
  );
};

function buildDefaultNotEnrolledReply(opts: { businessName?: string; botName?: string }): string {
  const business = (opts.businessName || '').trim();
  const bot = (opts.botName || '').trim();

  if (bot && business) {
    return `Hi! I'm ${bot} from ${business}. You're not registered with us on WhatsApp yet — please reach out and we'll get you set up.`;
  }
  if (business) {
    return `Hi! You're not registered with ${business} on WhatsApp yet — please reach out and we'll get you set up.`;
  }
  return "Hi! You're not registered with us on WhatsApp yet. Please contact us to get set up.";
}

export default ChannelNotConfigured;
